import { generate, type ToVariants } from '@/components/experiments/Core/generateBasicExperimentComponent';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { SwiperRule } from '@/helpers/promotion-helper';
import { useMounted } from '@vueuse/core';
import { computed, ref } from 'vue';
import { ExperimentTrackEventOptions } from '../Core/types';

// ?preferred_variant=FAUX_SWIPER_EXP:control
// ?preferred_variant=FAUX_SWIPER_EXP:variant_1
// ?preferred_variant=FAUX_SWIPER_EXP:variant_2

// ?remove_preferred_experiment=FAUX_SWIPER_EXP

export const FauxSwiperExpName = 'FAUX_SWIPER_EXP' as const;

export type FauxSwiperExpVariants = ToVariants<typeof FauxSwiperExpVariants>;

export const FauxSwiperExpVariants = {
	CONTROL: 'control',
	VARIANT_1: 'variant_1',
	VARIANT_2: 'variant_2',
} as const;

const FauxSwiperExp = generate(FauxSwiperExpName, FauxSwiperExpVariants);

export default FauxSwiperExp;

export const fauxExperimentSwiperRules: Record<string, SwiperRule> = {
	variant_1: {
		packages: [9, 9],
		slideDelay: 1000,
		rules: [
			{
				selectors: {
					browsers: [],
				},
				package: 9,
			},
		],
		default: {
			package: 9,
		},
	},
	variant_2: {
		packages: [9],
		slideDelay: 1000,
		rules: [
			{
				selectors: {
					browsers: [],
				},
				package: 9,
			},
		],
		default: {
			package: 9,
		},
	},
};

export function useFauxSwiperExp() {
	const isMounted = useMounted();
	const { country } = useLanguageStore();
	const { isArbitrating } = useArbitrage();
	const { isLoggedIn } = useUserStore();
	const { activeVariantsWithControlGroup } = useExperimentStore();
	const impressionFired = ref(false);

	const fauxSwiperExpVariant = computed(() => {
		if (!isMounted.value || !['DE', 'GB'].includes(country.value) || isLoggedIn.value || isArbitrating.value)
			return null;

		return activeVariantsWithControlGroup.value[FauxSwiperExpName] as FauxSwiperExpVariants;
	});

	function trackFauxSwiperExp(TrackingOptions: ExperimentTrackEventOptions<FauxSwiperExpVariants>) {
		if (fauxSwiperExpVariant.value) {
			if (TrackingOptions.action === 'impression' && impressionFired.value) return;

			FauxSwiperExp.trackEvent({
				property: 'faux-swiper-exp',
				...TrackingOptions,
			});
		}
	}

	return {
		fauxSwiperExpVariant,
		trackFauxSwiperExp,
	};
}

export type TrackFauxSwiperExp = ReturnType<typeof useFauxSwiperExp>['trackFauxSwiperExp'];

type FauxSwiperExpTags = { GB: string; DE: string };

export const fauxSwiperTags: Record<FauxSwiperExpVariants, FauxSwiperExpTags> = {
	control: { GB: 'amz-faux-con-21', DE: 'amz-faux-co-21' },
	variant_1: { GB: 'amz-faux-va1-21', DE: 'amz-faux-v1-21' },
	variant_2: { GB: 'amz-faux-va2-21', DE: 'amz-faux-v2-21' },
};

type FauxCountryCode = keyof FauxSwiperExpTags;

export function isFauxSwiperCountryTypeGuard(value: string): value is FauxCountryCode {
	return value === 'GB' || value === 'DE';
}
